import React, { useState, useRef, useEffect, useCallback, useContext } from "react";
import { setCookie } from '../cookieUtils.js';
import { log } from "../utils/logger.js";
import { AuthContext } from '../App.js';
import { getFlowNames } from "../api/fetchFlows.js";

function VaultSelector({ 
  currentVault, 
  onVaultChange, 
  userEmail, 
  styles, 
  isDarkMode, 
  showDeleteButton = true,
  showVaultLabel = true,
  wait = false,
  showSearch = true,
  vaults = ['-'], // Receive vaults as props
  setVaults = null // Optional function to update vaults in parent
}) {
  const { vectorVault } = useContext(AuthContext);
  const [newVaultName, setNewVaultName] = useState('');
  const [showCreateNewInput, setShowCreateNewInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [inputCode, setInputCode] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const newVaultInputRef = useRef(null);

  useEffect(() => {
    if (showCreateNewInput && newVaultInputRef.current) {
      setTimeout(() => {
        newVaultInputRef.current.focus();
      }, 100);
    }
  }, [showCreateNewInput]);

  // Build the ordered vault list: Create New, Search (if enabled), then actual vaults
  const orderedVaults = ['Create New'];
  if (showSearch) {
    orderedVaults.push('Search');
  }
  orderedVaults.push(...vaults.filter(vault => vault !== 'Create New' && vault !== 'Search'));

  // Validate current vault and auto-switch if needed
  useEffect(() => {
    const validVaults = orderedVaults.filter(vault => vault !== 'Create New' && vault !== 'Search');
    if (currentVault && !validVaults.includes(currentVault)) {
      const firstVault = validVaults.length > 0 ? validVaults[0] : '-';
      log('Current vault not found in list, switching to:', firstVault);
      updateVault(firstVault);
    }
    
    if (orderedVaults.length === 1 && orderedVaults[0] === 'Create New') {
      setShowCreateNewInput(true);
    }
  }, [vaults, currentVault]);

  const updateVault = useCallback((value) => {
    onVaultChange(value);
    setCookie("Ampersandposium", value);
  }, [onVaultChange]);

  const handleVaultChange = (e) => {
    const value = e.target.value;
    if (value === 'Create New') {
      setShowCreateNewInput(true);
      setNewVaultName('');
      setIsSearchMode(false);
      setSearchTerm('');
    } else if (value === 'Search') {
      setIsSearchMode(true);
      setShowCreateNewInput(false);
      setSearchTerm('');
    } else {
      setShowCreateNewInput(false);
      setIsSearchMode(false);
      setSearchTerm('');
      updateVault(value);
    }
  };

  const handleCreateNewVault = async () => {
    if (newVaultName) {
      setIsLoading(true);
      try {
        await vectorVault.createVault(newVaultName);
        
        const updatedVaults = [...vaults, newVaultName];
        
        // Update vaults list in state and cookie FIRST
        if (setVaults) {
          setVaults(updatedVaults);
        }
        setCookie("vaults", JSON.stringify(updatedVaults));
        
        // THEN update the current vault and set Ampersandposium cookie
        updateVault(newVaultName);
        setShowCreateNewInput(false);
        setNewVaultName('');
        
        alert(`Vault "${newVaultName}" created successfully!`);
      } catch (error) {
        console.error("Error creating vault:", error);
        alert(`Error creating vault: ${error.message || 'Unknown error'}`);
      } finally {
        setIsLoading(false);
      }
    } else {
      alert("Please enter a name for the new vault.");
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleCreateNewVault();
    }
  };

  const handleDelete = () => {
    setShowModal(true);
  };

  const confirmDeletion = async () => {
    if (inputCode === "DELETE") {
      setShowModal(false);
      setIsLoading(true);
      try {
        const response = await vectorVault.deleteVault(currentVault);
        log('delete response', response);
        
        // Remove the deleted vault from the list
        const updatedVaults = vaults.filter(vault => vault !== currentVault);
        if (setVaults) {
          setVaults(updatedVaults);
        }
        setCookie("vaults", JSON.stringify(updatedVaults));
        
        // Switch to first available vault
        const firstVault = updatedVaults.length > 0 ? updatedVaults[0] : '-';
        updateVault(firstVault);
        
        alert("Database deleted successfully.");
      } catch (error) {
        console.error("There was an error with the fetch operation:", error);
        alert("Error deleting database.");
      } finally {
        setIsLoading(false);
        setInputCode('');
      }
    } else {
      alert('You must type "DELETE" to confirm you want to delete the entire database');
      setInputCode('');
    }
  };

  // Filter vaults based on search term when in search mode
  const actualVaults = vaults.filter(v => v !== 'Create New' && v !== 'Search');
  const filteredVaults = isSearchMode && searchTerm 
    ? actualVaults.filter(vault => 
        vault.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : actualVaults;

  // For display in dropdown - always use orderedVaults unless in search mode
  const displayVaults = isSearchMode 
    ? (searchTerm ? ['Search', ...filteredVaults] : ['Search', ...actualVaults])
    : orderedVaults;

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div>
      {showVaultLabel && (
        <h3 style={{ marginBottom: "20px" }}>Vault: {currentVault}</h3>
      )}
      
      {/* Search input - shown when Search is selected */}
      {isSearchMode && !showCreateNewInput && (
        <div style={{ marginBottom: "10px", display: "flex", alignItems: "center", gap: "5px" }}>
          <input
            type="text"
            className={isDarkMode ? 'sleek-input-dark' : 'sleek-input-light'}
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search vaults..."
            style={{ width: '200px' }}
            autoFocus
          />
          {searchTerm && (
            <button 
              style={{ ...styles.btn, padding: "2px 8px", fontSize: "12px" }} 
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              ✕
            </button>
          )}
          <button 
            style={{ ...styles.btn, padding: "2px 8px", fontSize: "12px" }} 
            onClick={() => {
              setIsSearchMode(false);
              setSearchTerm('');
            }}
            title="Cancel search"
          >
            Cancel
          </button>
        </div>
      )}
      
      {/* Show message when search returns no results */}
      {isSearchMode && searchTerm && filteredVaults.length === 0 && (
        <div style={{ 
          marginBottom: "10px", 
          padding: "8px", 
          fontSize: "14px", 
          color: isDarkMode ? "#999999" : "#6b7280",
          fontStyle: "italic" 
        }}>
          No vaults found matching "{searchTerm}"
        </div>
      )}
      
      {showCreateNewInput ? (
        <div>
          <input
            ref={newVaultInputRef}
            type="text"
            className={isDarkMode ? 'sleek-input-dark' : 'sleek-input-light'}
            value={newVaultName}
            onChange={(e) => setNewVaultName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter new vault name"
            style={{ width: '200px' }}
          />
          <button style={{ ...styles.btn, marginLeft: '5px' }} onClick={handleCreateNewVault}>
            Create
          </button>
          <button style={{ ...styles.btn, marginLeft: '5px' }} onClick={() => setShowCreateNewInput(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <>
          <select
            className={isDarkMode ? 'sleek-select-dark' : 'sleek-select-light'}
            value={isSearchMode ? 'Search' : currentVault}
            onChange={handleVaultChange}
          >
            {Array.isArray(displayVaults) && displayVaults.map(vault => (
              <option key={vault} value={vault}>{vault}</option>
            ))}
          </select>
          {showDeleteButton && (
            <button style={{ ...styles.btn, marginLeft: "-5px" }} onClick={handleDelete}>
              Delete
            </button>
          )}
        </>
      )}

      {/* Loading Spinner */}
      <div className="loading-spinner" style={{ display: isLoading ? 'block' : 'none' }}>
        <svg viewBox="0 0 50 50">
          <circle cx="25" cy="25" r="20" stroke="#00d67a" strokeWidth="5" fill="none" />
        </svg>
      </div>

      {/* Delete Confirmation Modal */}
      {showModal && (
        <div className={isDarkMode ? "modall modall-dark" : "modall modall-light"}>
          <div className={isDarkMode ? "modal-content modal-content-dark" : "modal-content modal-content-light"}>
            <div className="modal-header">
              <h3>Delete Vault</h3>
              <button className={isDarkMode ? 'btn-dark' : 'btn-light'} onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <div className="modal-body">
              <p>Type "DELETE" below to confirm you want to permanently delete the entire database <b>{currentVault}</b>:</p>
              <input
                className={isDarkMode ? 'sleek-input-dark' : 'sleek-input-light'}
                type="text"
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value)}
              />
              <button onClick={confirmDeletion} style={{ marginLeft: '5px' }}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VaultSelector; 